<template>
  <div>
    <base-card no-header>
      <template #body>
        <el-row>
          <el-col :span="6">
            <h3>{{ $t('gain') }}</h3>
            <span class="body-1 cursor-pointer" @click="openVersionModal">
              {{
                $t('currentVersion') + ': ' + props.latestConfiguration.version
              }}
            </span>
          </el-col>
          <el-col :span="12">
            <el-form :model="form" label-position="top">
              <el-divider>{{ $t('transformationEngineSettings') }}</el-divider>
              <el-form-item
                :label="$t('transformationEngineTargetBranchName')"
                prop="inputFlow_Transformations_MainBranchName"
                class="mb-0"
              >
                <el-input
                  v-model="form.inputFlow_Transformations_MainBranchName"
                  size="small"
                />
              </el-form-item>
              <el-form-item
                :label="$t('transformationEngineBatchSize')"
                prop="inputFlow_TransformationEngine_BatchSize"
                class="mb-0"
              >
                <el-input-number
                  v-model="form.inputFlow_TransformationEngine_BatchSize"
                  :min="0"
                  size="small"
                />
              </el-form-item>
              <el-divider>{{ $t('sftpSettings') }}</el-divider>
              <el-form-item
                :label="$t('scanPath')"
                prop="sftpListener_ScanPath"
                class="mb-0"
              >
                <el-input v-model="form.sftpListener_ScanPath" size="small" />
              </el-form-item>
              <el-form-item
                :label="$t('archivePath')"
                prop="sftpArchiver_ArchivePath"
                class="mb-0"
              >
                <el-input
                  v-model="form.sftpArchiver_ArchivePath"
                  size="small"
                />
              </el-form-item>
              <el-divider>{{ $t('encryptionSettings') }}</el-divider>
              <el-form-item
                :label="$t('encryptionKey_FilePath')"
                prop="sftpEncryption_PgpFilePath"
                class="mb-0"
              >
                <el-tooltip
                  placement="top"
                  :content="$t('sftpPgpFilePath_TooltipContent')"
                >
                  <el-input
                    v-model="form.sftpEncryption_PgpFilePath"
                    size="small"
                  />
                </el-tooltip>
              </el-form-item>
              <el-form-item
                :label="$t('encryptionKey_Passphrase')"
                prop="sftpEncryption_PgpPassphrase"
                class="mb-0"
              >
                <el-input
                  v-model="form.sftpEncryption_PgpPassphrase"
                  type="password"
                  size="small"
                  show-password
                />
              </el-form-item>
              <template v-if="showMobilityFlaggingFeature">
                <el-divider>{{ $t('mobilityFlaggingSettings') }}</el-divider>
                <el-form-item
                  :label="$t('mF_SftpEncryption_PgpFilePath')"
                  prop="mF_SftpEncryption_PgpFilePath"
                  class="mb-0"
                >
                  <el-tooltip
                    placement="top"
                    :content="$t('sftpPgpFilePath_TooltipContent')"
                  >
                    <el-input
                      v-model="form.mF_SftpEncryption_PgpFilePath"
                      size="small"
                    />
                  </el-tooltip>
                </el-form-item>
                <el-form-item
                  :label="$t('mF_SftpEncryption_PgpPassphrase')"
                  prop="mF_SftpEncryption_PgpPassphrase"
                  class="mb-0"
                >
                  <el-input
                    v-model="form.mF_SftpEncryption_PgpPassphrase"
                    type="password"
                    size="small"
                    show-password
                  />
                </el-form-item>
              </template>
              <template v-if="showMobilityFlaggingFeature">
                <el-divider>{{ $t('destinationConfigurations') }}</el-divider>
                <template v-if="!form.destinationConfigurations?.length">
                  <el-button
                    type="primary"
                    size="small"
                    class="mt-2 cursor-pointer"
                    @click="addDestination"
                  >
                    + {{ $t('add') }}
                  </el-button>
                </template>
                <template v-else>
                  <el-form-item
                    v-for="(dest, idx) in form.destinationConfigurations"
                    :key="`dest-${idx}`"
                    class="mb-2"
                  >
                    <el-row :gutter="10" style="width: 100%">
                      <el-col :span="6">
                        <el-form-item class="mb-0" :label="$t('sftpType')">
                          <el-select
                            v-model="dest.sftpType"
                            size="small"
                            @change="handleSftpTypeChange(idx)"
                          >
                            <el-option
                              v-for="item in Object.keys(SftpType)"
                              :key="item"
                              :label="item"
                              :value="SftpType[item]"
                            />
                          </el-select>
                        </el-form-item>
                      </el-col>
                      <el-col :span="7">
                        <el-form-item class="mb-0" :label="$t('brokerId')">
                          <el-input
                            v-if="dest.sftpType === SftpType.Deloitte"
                            v-model="dest.brokerId"
                            size="small"
                            disabled
                          />
                          <el-select
                            v-else
                            v-model="dest.brokerId"
                            size="small"
                            filterable
                          >
                            <el-option
                              v-for="brokerId in brokerIdOptions"
                              :key="brokerId"
                              :label="brokerId"
                              :value="brokerId"
                            />
                          </el-select>
                        </el-form-item>
                      </el-col>
                      <el-col :span="7">
                        <el-form-item class="mb-0" :label="$t('folderPath')">
                          <el-input v-model="dest.folderPath" size="small" />
                        </el-form-item>
                      </el-col>
                      <el-col
                        :span="4"
                        style="padding-bottom: 8px"
                        class="destination-row-col"
                      >
                        <el-button
                          type="default"
                          size="small"
                          @click="removeDestination(idx)"
                        >
                          {{ $t('remove') }}
                        </el-button>
                      </el-col>
                    </el-row>
                  </el-form-item>
                  <el-button
                    type="primary"
                    size="small"
                    @click="addDestination"
                  >
                    + {{ $t('add') }}
                  </el-button>
                </template>
              </template>
            </el-form>
          </el-col>
          <el-col :span="6" class="d-flex">
            <el-button
              class="ml-auto"
              type="primary"
              @click="saveConfiguration"
            >
              <h4>{{ $t('save') }}</h4>
            </el-button>
          </el-col>
        </el-row>
      </template>
    </base-card>
    <gain-configuration-versions-modal
      v-model="showVersionModal"
      :configuration-versions="props.configurationHistory"
      @restore="handleRestore"
      @close="showVersionModal = false"
    />
  </div>
</template>
<script lang="ts">
import BaseCard from '@/components/panels/BaseCard.vue';
import GainConfigurationVersionsModal from './GainConfigurationVersionsModal.vue';
import { computed, onUpdated, ref } from 'vue';
import config from '@/config/config';
import {
  ElButton,
  ElCol,
  ElDivider,
  ElForm,
  ElFormItem,
  ElInput,
  ElInputNumber,
  ElOption,
  ElRow,
  ElSelect,
  ElTooltip
} from 'element-plus';
import {
  SystemConfiguration,
  DestinationConfiguration,
  SftpType,
  BrokerSftpConfiguration
} from '@etp/etp-gain-bff-client/axios';

export default {
  name: 'GainConfigurationCard',
  components: {
    BaseCard,
    ElButton,
    ElCol,
    ElDivider,
    ElForm,
    ElFormItem,
    ElInput,
    ElInputNumber,
    ElOption,
    ElRow,
    ElSelect,
    ElTooltip,
    GainConfigurationVersionsModal
  },
  props: {
    latestConfiguration: {
      type: Object as () => SystemConfiguration,
      required: true
    },
    configurationHistory: {
      type: Array as () => Array<SystemConfiguration>,
      default: () => new Array<SystemConfiguration>()
    },
    brokerSftpConfigurations: {
      type: Array as () => Array<BrokerSftpConfiguration>,
      default: () => new Array<BrokerSftpConfiguration>()
    }
  },
  emits: ['update', 'fetch-history'],
  setup(props, { emit }) {
    const showVersionModal = ref(false);
    const showMobilityFlaggingFeature = computed(
      () => config.features.showMobilityFlaggingFeature
    );

    interface DestinationConfigurationForm {
      sftpType: SftpType;
      brokerId: string;
      folderPath: string;
    }

    const form = ref({
      inputFlow_TransformationEngine_BatchSize: 0,
      inputFlow_Transformations_MainBranchName: '',
      sftpListener_ScanPath: '',
      sftpArchiver_ArchivePath: '',
      sftpEncryption_PgpFilePath: '',
      sftpEncryption_PgpPassphrase: '',
      mF_SftpEncryption_PgpFilePath: '',
      mF_SftpEncryption_PgpPassphrase: '',
      destinationConfigurations: [] as Array<DestinationConfigurationForm>
    });

    const brokerIdOptions = computed(
      (): Array<string> =>
        props.brokerSftpConfigurations
          .filter(config => config.enabled !== false)
          .map(config => config.brokerId)
          .filter((x): x is string => !!x)
    );

    const openVersionModal = () => {
      emit('fetch-history');
      showVersionModal.value = true;
    };

    const handleRestore = (input: SystemConfiguration) => {
      emit('update', input);
      showVersionModal.value = false;
    };

    const addDestination = () => {
      form.value.destinationConfigurations?.push({
        sftpType: SftpType.Deloitte,
        brokerId: 'Deloitte',
        folderPath: ''
      });
    };

    const removeDestination = (index: number) => {
      form.value.destinationConfigurations?.splice(index, 1);
    };

    const handleSftpTypeChange = (index: number) => {
      if (
        form.value.destinationConfigurations?.[index].sftpType ===
        SftpType.Deloitte
      ) {
        form.value.destinationConfigurations[index].brokerId = 'Deloitte';
      }
    };

    const saveConfiguration = async () => {
      const destinationConfigurations: Array<DestinationConfiguration> =
        form.value.destinationConfigurations?.map(dest => ({
          sftpType: dest.sftpType,
          brokerId: dest.brokerId,
          folderPath: dest.folderPath
        })) ?? [];

      emit('update', {
        ...props.latestConfiguration,
        ...form.value,
        destinationConfigurations
      } as SystemConfiguration);
    };

    onUpdated(() => {
      const config = props.latestConfiguration;
      form.value.inputFlow_TransformationEngine_BatchSize =
        config.inputFlow_TransformationEngine_BatchSize ?? 0;
      form.value.inputFlow_Transformations_MainBranchName =
        config.inputFlow_Transformations_MainBranchName ?? '';
      form.value.sftpListener_ScanPath = config.sftpListener_ScanPath ?? '';
      form.value.sftpArchiver_ArchivePath =
        config.sftpArchiver_ArchivePath ?? '';
      form.value.sftpEncryption_PgpFilePath =
        config.sftpEncryption_PgpFilePath ?? '';
      form.value.sftpEncryption_PgpPassphrase =
        config.sftpEncryption_PgpPassphrase ?? '';
      form.value.mF_SftpEncryption_PgpFilePath =
        config.mF_SftpEncryption_PgpFilePath ?? '';
      form.value.mF_SftpEncryption_PgpPassphrase =
        config.mF_SftpEncryption_PgpPassphrase ?? '';
      form.value.destinationConfigurations =
        config.destinationConfigurations?.map(dest => ({
          sftpType: dest.sftpType ?? SftpType.Deloitte,
          brokerId: dest.brokerId ?? '',
          folderPath: dest.folderPath ?? ''
        })) ?? [];
    });

    return {
      addDestination,
      brokerIdOptions,
      form,
      handleRestore,
      handleSftpTypeChange,
      openVersionModal,
      props,
      removeDestination,
      saveConfiguration,
      SftpType,
      showMobilityFlaggingFeature,
      showVersionModal
    };
  }
};
</script>
<style scoped lang="scss">
.el-col.destination-row-col {
  display: flex;
  align-self: end;
  justify-content: center;
}
</style>
